import React from "react";
import {
  Gavel,
  TrendingUp,
  FileText,
  Calculator,
  Globe,
  Activity,
  Layers,
  CheckCircle2,
} from "lucide-react";
import "./auction.css";

export default function Auction() {
  const upcomingFeatures = [
    {
      icon: <Activity size={24} className="auction-feature-icon" />,
      title: "Live Multi-Lane Bidding",
      desc: "Simultaneous real-time monitoring across 130+ Japanese auction houses including USS, TAA, CAA, ARAI, and JU with sub-second bid placement."
    },
    {
      icon: <FileText size={24} className="auction-feature-icon" />,
      title: "AI Inspection Sheet Translation",
      desc: "Automated OCR parsing of handwritten Japanese auction sheets, grades, scratch/dent condition charts, and mechanical inspection remarks."
    },
    {
      icon: <TrendingUp size={24} className="auction-feature-icon" />,
      title: "Market Valuation & Sales Trends",
      desc: "Access millions of historical auction transactions, price trajectory analytics by grade/mileage, and accurate ex-yard market projections."
    },
    {
      icon: <Calculator size={24} className="auction-feature-icon" />,
      title: "Automated Landed Cost Engine",
      desc: "Instant breakdown of auction hammer fees, inland Japanese transportation, container or RoRo freight, and regional customs calculations."
    }
  ];

  const milestones = [
    { name: "Japanese Auction Data Feeds (USS / TAA)", status: "Completed" },
    { name: "Persistent Secure Session Reverse Proxy", status: "Completed" },
    { name: "Inspection Sheet AI Parser & OCR", status: "In Testing" },
    { name: "Staff Bid Limits & Watchlist Integration", status: "In Progress" },
    { name: "Production Gateway & CRM Sync", status: "Staging" }
  ];

  return (
    <div className="auction-coming-soon-wrapper">
      {/* Background glow and subtle mesh */}
      <div className="auction-glow-bg" />

      <div className="auction-container">
        {/* Top Header Badge */}
        <div className="auction-badge-row">
          <span className="auction-pill">
            <span className="auction-pill-dot" />
            ENTERPRISE MODULE UNDER DEVELOPMENT
          </span>
          <span className="auction-version-tag">Target: Q1 2027</span>
        </div>

        {/* Hero Title & Description */}
        <div className="auction-hero-section text-center">
          <div className="auction-hero-icon-bubble">
            <Gavel size={38} className="auction-hero-gavel" />
          </div>
          <h1 className="auction-hero-title">Japanese Auction Live Portal</h1>
          <p className="auction-hero-subtitle">
            Platinum Auto Japan is integrating direct high-speed auction feeds with major Japanese vehicle auction networks. 
            Automated procurement, translation intelligence, and live multi-lane terminal access will be accessible directly from this CRM workspace.
          </p>
        </div>

        {/* 4 Feature Highlights Grid */}
        <div className="auction-grid">
          {upcomingFeatures.map((feat, idx) => (
            <div key={idx} className="auction-card">
              <div className="auction-card-header">
                <div className="auction-icon-wrap">
                  {feat.icon}
                </div>
                <h3 className="auction-card-title">{feat.title}</h3>
              </div>
              <p className="auction-card-desc">{feat.desc}</p>
            </div>
          ))}
        </div>

        {/* Pipeline & Milestones Progress Card */}
        <div className="auction-milestones-card">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <Layers size={18} className="text-primary" />
              <span className="fw-semibold text-light" style={{ fontSize: "14px" }}>
                Integration &amp; Deployment Roadmap
              </span>
            </div>
            <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-1" style={{ fontSize: "11px" }}>
              Active Sprint
            </span>
          </div>

          <div className="row g-2">
            {milestones.map((m, i) => (
              <div key={i} className="col-12 col-md-6 col-lg">
                <div className="auction-milestone-pill">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <CheckCircle2
                      size={14}
                      className={
                        m.status === "Completed"
                          ? "text-success"
                          : m.status === "In Testing" || m.status === "In Progress"
                          ? "text-warning"
                          : "text-muted"
                      }
                    />
                    <span className="auction-milestone-status">{m.status}</span>
                  </div>
                  <div className="auction-milestone-name">{m.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Notice Footer */}
        <div className="auction-footer-note text-center">
          <Globe size={15} className="text-muted me-1" />
          <span>
            For priority manual bids or urgent auction sheet inquiries, please reach out directly to the Auction Desk via the internal Team Chat or management staff.
          </span>
        </div>
      </div>
    </div>
  );
}
